<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <EditableComponent
    v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
    :id="id"
    :active="componentEditMode && componentEditMode?.id === id"
    :duplicate="componentPermissions.canDuplicate"
    :edit="canEdit"
    :remove="componentPermissions.canRemove"
    :selected="componentSelectedMode && componentSelectedMode?.id === id"
    :settings="!usedInSectionId"
    :stylable="!usedInSectionId"
    :style="omit(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')"
    :variants="
      componentPermissions.canEdit
        ? {
            title: t('canvasUI.canvasBuilder.grid.selectLayout'),
            options: DISPLAY_OPTIONS,
            selected: contentGridData?.layout_type ?? ContentGridLayoutTypes.GRID,
          }
        : undefined
    "
    @duplicate="duplicate(id)"
    @edit="onSelectFilesForGrid"
    @remove="removeComponentById(id)"
    @select="setComponentSelectedMode(id)"
    @settings="setComponentSettingsMode(id)"
    @style="emit('style', $event)"
    @variant-change="onVariantChange"
  >
    <RawContentGrid
      v-if="contentGridData?.items?.length"
      :data="contentGridData"
      :style="pick(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')"
      :used-in-section-id="usedInSectionId"
      v-bind="$attrs"
      @deleted-file-clicked="onSelectFilesForGrid"
    />
    <EditContentGrid v-else v-bind="$attrs" />
  </EditableComponent>
  <RawContentGrid
    v-else
    :data="contentGridData"
    v-bind="$attrs"
    :style="presentationModeStyle"
    :used-in-section-id="usedInSectionId"
    @deleted-file-clicked="onSelectFilesForGrid"
  />
</template>

<script setup lang="ts">
import { h, toRefs, onMounted, computed, watch, inject, type CSSProperties, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DropdownOption } from 'naive-ui'
import { omit, pick, get } from 'lodash-es'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import { FileContentTypeEnum, type CanvasRetrieve } from '@openapi'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import EditContentGrid from './ContentGrid.edit.vue'
import RawContentGrid from './ContentGrid.raw.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useContentSelector from '@canvas-builder/composables/useContentSelector'
import { ContentGridLayoutTypes } from '@canvas-builder/types/canvas'
import { type ContentGridProps, type ComponentNode, ComponentTypes } from '@canvas-builder/types/canvas'
import type { Selection } from '@lib/types/selections'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useToast from '@lib/composables/useToast'
import { transformFilesToContentGrid } from '@collection-selector/utils/content-selector-adapter.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useFileInfo, { type ResolveRequest, type ResolveResponse } from '@lib/composables/useFileInfo'

const props = withDefaults(
  defineProps<{
    id: string
    data: ContentGridProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: CSSProperties
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)
const emit = defineEmits<{
  style: [s: CSSProperties]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const resolveEntities = inject<(request: ResolveRequest) => Promise<ResolveResponse>>('resolveEntities')
const pitcherInfo = inject<ComputedRef<{ instanceId: string } | undefined>>(
  'pitcherInfo',
  computed(() => undefined),
)

const { toast } = useToast()
const { t } = useI18n()
const {
  activeCanvas,
  mode,
  componentEditMode,
  componentSelectedMode,
  isEditMode,
  isViewOnlyMode,
  isTemplate,
  justAddedComponentId,
  sectionComponentOverrides,
  duplicate,
  removeComponentById,
  setComponentSelectedMode,
  setComponentSettingsMode,
  updateNodeDataById,
  fileDataCache,
} = useCanvas()
const { standaloneSectionContentGridsWithMatchingPropertiesPreviewData } = useRecommendations()

// Set up file resolution
const { resolveFileIds } = useFileInfo({
  resolveFetcher: resolveEntities,
  instanceId: pitcherInfo.value?.instanceId,
})

// Track files that are being resolved to avoid duplicate requests
const resolvingFileIds = new Set<string>()

// Function to batch resolve files
const batchResolveFiles = (fileIds: string[]) => {
  const idsToResolve = fileIds.filter((id) => id && !fileDataCache.value.get(id) && !resolvingFileIds.has(id))

  if (idsToResolve.length > 0 && resolveEntities && pitcherInfo.value?.instanceId) {
    // Mark these IDs as being resolved
    idsToResolve.forEach((id) => resolvingFileIds.add(id))

    resolveFileIds(idsToResolve)
      .then((resolvedFiles) => {
        resolvedFiles.forEach((file) => {
          fileDataCache.value.set(file.id, file)
          resolvingFileIds.delete(file.id)
        })
      })
      .catch(() => {
        // On error, remove from resolving set so they can be retried
        idsToResolve.forEach((id) => resolvingFileIds.delete(id))
      })
  }
}

const baseContentGridData = computed<ContentGridProps>(() => {
  // if it's a standalone section and it has preview recommendations, show them
  if (
    activeCanvas.value?.is_section &&
    !props.data.items?.length &&
    !!standaloneSectionContentGridsWithMatchingPropertiesPreviewData.value[props.id]
  ) {
    return standaloneSectionContentGridsWithMatchingPropertiesPreviewData.value[props.id]
  } else if (!props.usedInSectionId || !sectionComponentOverrides.value?.[props.usedInSectionId]?.[props.id]) {
    return props.data
  } else {
    return sectionComponentOverrides.value[props.usedInSectionId][props.id].node?.data as ContentGridProps
  }
})

const contentGridDataResult = computed<{ data: ContentGridProps; error?: string | null }>(() => {
  if (isTemplate.value || !launchDarkly.value.enable_content_grid_data_accessor) {
    return { data: baseContentGridData.value }
  }

  if (!baseContentGridData.value.data_accessor) {
    return { data: baseContentGridData.value }
  }

  try {
    const contextData = get(activeCanvas.value?.context, baseContentGridData.value.data_accessor)

    if (!contextData) {
      throw new Error()
    }

    const transformedData = transformFilesToContentGrid(contextData)

    // Batch resolve all files that need resolution
    const allFileIds = transformedData.items.map((item) => item.file.id)
    batchResolveFiles(allFileIds)

    // Transform items to include proper thumbnail URLs from resolved files
    const transformedItems = transformedData.items.map((item) => {
      const cachedFileData = fileDataCache.value.get(item.file.id)

      if (!cachedFileData) {
        return item // File not resolved yet
      }

      if (item.type === 'slide' && typeof item.slide.index === 'number') {
        // For slide items, set the thumbnail URL from content_thumbnails[index]
        const thumbnailUrl = cachedFileData.content_thumbnails?.[item.slide.index] || null

        return {
          ...item,
          file: {
            ...item.file,
            ...cachedFileData,
          },
          slide: {
            ...item.slide,
            url: thumbnailUrl,
          },
        }
      } else {
        // For file items, merge resolved file data
        return {
          ...item,
          file: {
            ...item.file,
            ...cachedFileData,
          },
        }
      }
    })

    return {
      data: {
        ...transformedData,
        items: transformedItems,
        layout_type: baseContentGridData.value.layout_type,
        size: baseContentGridData.value.size,
        data_accessor: baseContentGridData.value.data_accessor,
      },
      error: null,
    }
  } catch (error) {
    return {
      data: baseContentGridData.value,
      error: t('canvasUI.canvasBuilder.contentGrid.errorAccessingData', {
        path: baseContentGridData.value.data_accessor,
      }),
    }
  }
})

const contentGridData = computed<ContentGridProps>(() => contentGridDataResult.value.data)

watch(
  () => contentGridDataResult.value.error,
  (newError, oldError) => {
    if (newError && newError !== oldError) {
      toast.error(newError)
    }
  },
)
const presentationModeStyle = computed(() => {
  return !isEditMode.value || props.usedInSectionId ? props.style : undefined
})
const { setContentSelectorMode } = useContentSelector()
const { id, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const canEdit = computed(
  () =>
    componentPermissions.value.canEdit &&
    (!launchDarkly.value.enable_content_grid_data_accessor || !contentGridDataResult.value.data.data_accessor),
)

const DISPLAY_OPTIONS = [
  {
    label: t('canvasUI.canvasBuilder.contentGrid.layoutNames.grid'),
    key: ContentGridLayoutTypes.GRID,
    icon: () => h(CIcon, { icon: 'fas fa-table-columns', size: '18px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.contentGrid.layoutNames.carousel'),
    key: ContentGridLayoutTypes.CAROUSEL,
    icon: () => h(CIcon, { icon: 'fas fa-gallery-thumbnails', size: '18px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.contentGrid.layoutNames.list'),
    key: ContentGridLayoutTypes.LIST,
    icon: () => h(CIcon, { icon: 'fas fa-list', size: '18px' }),
  },
]

function onSelectFilesForGrid() {
  setContentSelectorMode({
    id: props.id,
    componentType: ComponentTypes.ContentGrid,
    selections: getSelections(contentGridData.value?.items),
    allowedSelectorTypes: ['content'],
    allowedContentTypes: [
      FileContentTypeEnum.AR,
      FileContentTypeEnum.PDF,
      FileContentTypeEnum.VIDEO,
      FileContentTypeEnum.WEB,
    ],
  })
}

function onVariantChange(selection?: DropdownOption['key']) {
  const variant = selection as ContentGridLayoutTypes
  updateNodeDataById(props.id, { ...contentGridData.value, layout_type: variant })
}

function getSelections(items: ContentGridProps['items']): Selection[] {
  if (!items?.length) return []

  return items.reduce((acc, i) => {
    if (i.file.is_deleted) return acc
    if (i.type === 'slide') {
      return [
        ...acc,
        {
          fileId: i.file.id,
          type: 'page',
          pageIndex: i.slide.index,
        },
      ]
    }

    return [
      ...acc,
      {
        fileId: i.file.id,
        type: 'file',
      },
    ]
  }, [] as Selection[])
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    onSelectFilesForGrid()
  }
})
</script>
